import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';

/**
 * « Choisir une société » — juste après la connexion, pour les personnes rattachées à PLUSIEURS sociétés
 * (lot 1, étape 1.7, D1.7-a). Une seule société → cette page n'est jamais montrée.
 * La dernière société choisie sur cet appareil est signalée. Les dormantes sont visibles, grisées, non ouvrables.
 */
export const ChoisirSocietePage = () => {
  const { user, societes, loadSocietes, changerSociete } = useAuthStore();
  const navigate = useNavigate();
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!societes) loadSocietes().catch((e) => setErreur(extractError(e).message));
  }, [societes, loadSocietes]);

  if (!user) return <Navigate to="/login" replace />;

  let derniere: string | null = null;
  try { derniere = localStorage.getItem(`simgroup.derniereSociete.${user.id}`); } catch { /* sans effet */ }

  const ouvrir = async (filialeKey: string) => {
    setEnCours(filialeKey); setErreur(null);
    try {
      await changerSociete(filialeKey);
      navigate('/', { replace: true });
    } catch (e) {
      setErreur(extractError(e).message);
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div
        className="w-full max-w-lg p-8"
        style={{ background: 'var(--surface)', border: '1px solid var(--bdr)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-lg)' }}
      >
        <div className="mb-6 text-center">
          <img src="/assets/logos/simgroup-logo-officiel.svg" alt="SIM GROUP" className="mx-auto h-12 object-contain" />
          <h1 className="font-d mt-4" style={{ fontSize: 22, fontWeight: 700, color: 'var(--tx)' }}>Choisir une société</h1>
          <p style={{ fontSize: 13, color: 'var(--tx-3)', marginTop: 4 }}>
            Vous pourrez en changer à tout moment, en haut de la barre de gauche.
          </p>
        </div>

        {erreur && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}>
            <div className="ds-alert-txt">{erreur}</div>
          </div>
        )}

        {!societes && !erreur && (
          <div style={{ textAlign: 'center', color: 'var(--tx-3)', padding: 24 }}>
            <RefreshCw size={18} className="animate-spin" style={{ display: 'inline' }} /> Chargement…
          </div>
        )}

        <div style={{ display: 'grid', gap: 8 }}>
          {societes?.map((s) => (
            <button
              key={s.filialeKey}
              type="button"
              disabled={!s.ouvrable || enCours !== null}
              onClick={() => void ouvrir(s.filialeKey)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                padding: '12px 14px', borderRadius: 10,
                border: `1px solid ${s.ouvrable ? 'var(--bdr)' : 'transparent'}`,
                borderLeft: `4px solid ${s.ouvrable ? s.colorPrimary : 'var(--bdr)'}`,
                background: s.ouvrable ? 'var(--surface)' : 'var(--bg)',
                opacity: s.ouvrable ? 1 : 0.55,
                cursor: s.ouvrable ? 'pointer' : 'not-allowed',
              }}
            >
              <Building2 size={18} style={{ color: s.ouvrable ? s.colorPrimary : 'var(--tx-3)', flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--tx)' }}>
                  {s.name}
                  {s.origine && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--tx-3)' }}>ma société</span>}
                  {derniere === s.filialeKey && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--p, #1a5c3a)' }}>dernière utilisée</span>}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--tx-3)' }}>
                  {s.metierLabel}
                  {s.statut === 'dormante' && ' · Dormante — pas encore lancée'}
                  {s.ouvrable && !s.origine && ' · lecture seule par défaut'}
                </span>
              </span>
              {s.ouvrable && (enCours === s.filialeKey
                ? <RefreshCw size={16} className="animate-spin" />
                : <ChevronRight size={16} style={{ color: 'var(--tx-3)' }} />)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
